import {
  Box,
  Flex,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import React from "react";
import { HiOutlineDocumentText } from "react-icons/hi2";

import type { PendingDuplicateJd } from "./useAgenticChat";

type DuplicateJobDescriptionModalProps = {
  pending: PendingDuplicateJd | null;
  onKeepExisting: () => void;
  onUseNew: () => void;
};

const FileCard: React.FC<{
  fileName: string;
  label: string;
  isHighlighted?: boolean;
}> = ({ fileName, label, isHighlighted = false }) => (
  <Box
    px="3"
    py="2.5"
    borderRadius="md"
    border="1px solid"
    borderColor={isHighlighted ? "blue.200" : "gray.200"}
    bg={isHighlighted ? "blue.50" : "gray.50"}
  >
    <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb="1">
      {label}
    </Text>
    <Flex align="center" gap="2">
      <Icon as={HiOutlineDocumentText} boxSize="4" color="gray.500" />
      <Text fontSize="sm" fontWeight="medium" color="gray.800" noOfLines={1}>
        {fileName}
      </Text>
    </Flex>
  </Box>
);

export const DuplicateJobDescriptionModal: React.FC<
  DuplicateJobDescriptionModalProps
> = ({ pending, onKeepExisting, onUseNew }) => (
  <Modal
    isOpen={pending !== null}
    onClose={onKeepExisting}
    isCentered
    size="md"
  >
    <ModalOverlay bg="blackAlpha.400" />
    <ModalContent borderRadius="xl" mx="4" overflow="hidden">
      <ModalHeader
        pb="0"
        pt="5"
        px="6"
        fontSize="lg"
        fontWeight="bold"
        color="gray.800"
      >
        Replace job description?
      </ModalHeader>
      <ModalBody px="6" pt="3" pb="6">
        <Text fontSize="sm" color="gray.600" mb="4">
          Only one job description can be used per interview guide. Choose which
          one to keep.
        </Text>

        <Flex direction="column" gap="2" mb="5">
          <FileCard
            label="CURRENT"
            fileName={pending?.existingFileName ?? ""}
            isHighlighted
          />
          <FileCard label="NEW UPLOAD" fileName={pending?.newFileName ?? ""} />
        </Flex>

        <Flex gap="3">
          <Flex
            as="button"
            flex="1"
            align="center"
            justify="center"
            h="10"
            borderRadius="lg"
            border="1px solid"
            borderColor="gray.300"
            bg="white"
            color="gray.700"
            fontSize="sm"
            fontWeight="semibold"
            cursor="pointer"
            transition="all 0.15s"
            _hover={{ bg: "gray.50", borderColor: "gray.400" }}
            _active={{ bg: "gray.100" }}
            onClick={onKeepExisting}
          >
            Keep current
          </Flex>
          <Flex
            as="button"
            flex="1"
            align="center"
            justify="center"
            h="10"
            borderRadius="lg"
            bg="accent.600"
            color="white"
            fontSize="sm"
            fontWeight="semibold"
            cursor="pointer"
            transition="all 0.15s"
            _hover={{ bg: "#4330C4" }}
            _active={{ bg: "#3928AA" }}
            onClick={onUseNew}
          >
            Use new
          </Flex>
        </Flex>
      </ModalBody>
    </ModalContent>
  </Modal>
);
